import { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { useAuthStore } from '@/stores/auth'
import { useTeamStore } from '@/stores/team'
import { useUIStore } from '@/stores/ui'
import { useIsInternalAdmin } from '@/hooks/useTrialStatus'
import { UPDATE_TEAM } from '@/graphql/mutations/teams'
import { startTrial, TRIAL_LENGTH_DAYS } from '@/lib/trial'
import { Button } from '@/components/ui/Button'
import { IconSend, IconUsers, IconBrandYoutube, IconX } from '@/components/icons'

const BACKGROUND_IMAGE_URL = 'https://rolla-branding.s3.amazonaws.com/Admin%20Portal%20Assets/trialBackgroundImage.jpg'

const FEATURES = [
  {
    icon: IconSend,
    title: 'Launch Campaign',
    description: 'Launch and track video campaigns with ease.',
  },
  {
    icon: IconUsers,
    title: 'Unlimited Users',
    description: 'Activate unlimited users to capture clips for your brand.',
  },
  {
    icon: IconBrandYoutube,
    title: 'Access Video Clips',
    description: 'Download, edit and share your collected clips.',
  },
]

// Forces any admin on a team with no trial/subscription (hasNoPlan(team.planName)) to start a
// trial before using the app — see Layout.tsx. Grants the trial in our own DB immediately via
// updateTeam so the admin can use the portal right away, independent of whether the Zapier ->
// Chargebee leg succeeds; see the TEMPORARY note above.
export function StartTrialModal({ previewOnly, onClose }: { previewOnly?: boolean; onClose?: () => void } = {}) {
  const team = useTeamStore((s) => s.team)
  const profile = useAuthStore((s) => s.profile)
  const addToast = useUIStore((s) => s.addToast)
  // Internal/global admins are never meant to be forced through this modal (Layout.tsx keeps it
  // from showing for them in the normal case) — if a loading-state race slips it through anyway,
  // they still need a way to close it rather than being stuck, independent of whether the caller
  // remembered to wire up an onClose/previewOnly for that case.
  const isInternalAdmin = useIsInternalAdmin()
  const canClose = previewOnly || isInternalAdmin
  // Falls back to local state when the caller doesn't track dismissal itself (the real,
  // Layout.tsx-forced instance has no onClose at all — it's only ever unmounted via its own
  // noPlan/isInternalAdmin gating, which this exists as a backstop against).
  const [dismissed, setDismissed] = useState(false)
  const handleClose = onClose ?? (() => setDismissed(true))
  const [loading, setLoading] = useState(false)
  // CSS background-image loads independently of the DOM element painting — without this, the
  // white card renders instantly against a blank background for a beat while the (large, S3)
  // image is still fetching, then the photo pops in behind it. Preload it via a real Image
  // object first and only reveal the modal once it's ready.
  const [backgroundLoaded, setBackgroundLoaded] = useState(false)
  useEffect(() => {
    const img = new Image()
    img.onload = () => setBackgroundLoaded(true)
    img.onerror = () => setBackgroundLoaded(true)
    img.src = BACKGROUND_IMAGE_URL
    if (img.complete) setBackgroundLoaded(true)
  }, [])
  const [updateTeam] = useMutation(UPDATE_TEAM, {
    refetchQueries: ['GetTeam'],
    awaitRefetchQueries: true,
  })

  async function handleStartTrial() {
    if (previewOnly || !team?.id) return
    setLoading(true)
    try {
      const errorMessage = await startTrial(updateTeam, team.id, profile?.id, profile?.email)
      if (errorMessage) {
        addToast({ type: 'error', message: errorMessage })
        setLoading(false)
        return
      }
      // On success, leave loading true — refetchQueries updates team.planName, which unmounts
      // this modal (see Layout.tsx's gating condition) before the button would re-render anyway.
    } catch {
      addToast({ type: 'error', message: 'Failed to start trial. Please try again.' })
      setLoading(false)
    }
  }

  // Don't paint anything — not even a blank overlay — until the background image is ready, so
  // the white card never has a beat to appear against nothing. Also don't paint once dismissed
  // by an admin via the local-state fallback above.
  if (!backgroundLoaded || dismissed) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-start bg-cover bg-center p-4 sm:pl-16"
      style={{ backgroundImage: `url(${BACKGROUND_IMAGE_URL})` }}
      onClick={canClose ? handleClose : undefined}
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl bg-white p-8 text-center shadow-2xl sm:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        {canClose && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <IconX className="h-5 w-5" />
          </button>
        )}
        <h2 className="text-2xl font-bold text-gray-900">Start your free trial</h2>
        <p className="mt-2 text-sm text-gray-500">{TRIAL_LENGTH_DAYS} days of full access to Rolla.</p>

        <div className="mt-8 grid grid-cols-3 gap-4 border-t border-gray-100 pt-8">
          {FEATURES.map((feature) => (
            <div key={feature.title} className="flex flex-col items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <feature.icon className="h-5 w-5 text-gray-600" />
              </div>
              <p className="mt-3 text-sm font-semibold text-gray-900">{feature.title}</p>
              <p className="mt-1 text-xs text-gray-500">{feature.description}</p>
            </div>
          ))}
        </div>

        <Button
          variant="primary"
          size="lg"
          onClick={handleStartTrial}
          disabled={previewOnly || loading}
          className="mt-8 w-full justify-center"
        >
          {loading ? 'Starting trial...' : 'Start Trial'}
        </Button>
        <p className="mt-3 text-xs text-gray-400">
          {previewOnly ? 'Preview only — this button is disabled.' : '(No credit card required.)'}
        </p>
      </div>
    </div>
  )
}
